import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Table, Btn } from "@/components/UI";
import { AlertTriangle, SlidersHorizontal, ShoppingCart } from "lucide-react";
import { ExplicitFilterBar } from "@/components/common/ExplicitFilterBar";
import { StockAdjustModal } from "@/components/stock/StockAdjustModal";
import { STOCK_STATUS, fmtQty, toQuery } from "@/lib/products";
import { apiError } from "@/lib/customers";
import { useProductCapabilities } from "@/lib/useProductCapabilities";

const INITIAL = { q: "", critical: false, status: "" };
const FIELDS = [
  { key: "q", label: "Ürün", width: "w-64" },
  { key: "status", label: "Stok Durumu", type: "select", width: "w-40", options: [
    { value: "", label: "Tümü" }, { value: "ok", label: "Normal" }, { value: "critical", label: "Kritik Stok" }, { value: "out", label: "Stok Yok" }] },
  { key: "critical", label: "Yalnız kritik olanlar", type: "checkbox", width: "w-48" },
];

function StatusCell({ r }) {
  const s = STOCK_STATUS[r.status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold ${s.cls}`} data-testid={`stock-status-${r.product_id}`}>
      {r.is_critical && <AlertTriangle size={12} className="text-rose-600" data-testid={`stock-critical-icon-${r.product_id}`} />}{s.label}
    </span>
  );
}

export function StockTab() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(INITIAL);
  const [adjust, setAdjust] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const caps = useProductCapabilities();
  const nav = useNavigate();
  const load = (f = filters) => api.get(`/stock${toQuery(f)}`).then((r) => { setRows(r); setLoadErr(""); })
    .catch((e) => setLoadErr(apiError(e, "Stok yüklenemedi")));
  useEffect(() => { load(INITIAL); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const critCount = rows.filter((r) => r.is_critical).length;

  return (
    <div data-testid="stock-tab">
      <ExplicitFilterBar fields={FIELDS} initial={INITIAL} onApply={(f) => { setFilters(f); load(f); }} pdfPath="/stock/pdf" pdfName="Stok_Listesi.pdf" testid="stock-filter" />
      {loadErr && <div className="mb-3 text-sm text-rose-600" data-testid="stock-load-error">{loadErr}</div>}
      {critCount > 0 && <div className="mb-3 text-sm text-rose-700 flex items-center gap-1.5" data-testid="stock-critical-count"><AlertTriangle size={14} /> {critCount} ürün kritik stok seviyesinde</div>}
      <Table testid="stock-table" empty="Ürün bulunamadı"
        columns={[
          { label: "Ürün", render: (r) => <div><div className="font-medium" data-testid={`stock-name-${r.product_id}`}>{r.name}</div><div className="font-mono text-[11px] text-slate-500">{r.code}</div></div> },
          { label: "Mevcut Stok", render: (r) => <span data-testid={`stock-onhand-${r.product_id}`}>{fmtQty(r.on_hand)}</span> },
          { label: "Rezerve Stok", render: (r) => <span data-testid={`stock-reserved-${r.product_id}`}>{fmtQty(r.reserved)}</span> },
          { label: "Kalan Stok", render: (r) => <b className={r.is_critical ? "text-rose-600" : ""} data-testid={`stock-available-${r.product_id}`}>{fmtQty(r.available)}</b> },
          { label: "Kritik Stok", render: (r) => fmtQty(r.critical_stock) },
          { label: "Durum", render: (r) => <StatusCell r={r} /> },
          { label: "İşlem", render: (r) => (
            <div className="flex gap-1">
              {caps.stock_adjust && <Btn size="sm" variant="secondary" onClick={() => setAdjust(r)} data-testid={`stock-adjust-${r.product_id}`}><SlidersHorizontal size={12} /> Stok Düzeltme</Btn>}
              {r.is_critical && <Btn size="sm" variant="secondary" onClick={() => nav(`/purchasing?product_id=${r.product_id}&qty=${Math.max(1, r.critical_stock - r.available)}`)} data-testid={`stock-po-${r.product_id}`}><ShoppingCart size={12} /> Satın Alma Oluştur</Btn>}
            </div>
          ) },
        ]}
        rows={rows} />
      <StockAdjustModal row={adjust} onClose={() => setAdjust(null)} onSaved={() => { setAdjust(null); load(); }} />
    </div>
  );
}
